import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import useTimer from '../../hooks/useTimer';
import { getCurrentTime } from '../../utils/utils';

const TimerText = styled.span`
  font-family: ${({ isAiInterview }) =>
    isAiInterview ? 'Lato' : 'Open Sans, sans-serif'};
  ${({ isAiInterview }) =>
    isAiInterview &&
    css`
      font-weight: 600;
      font-style: SemiBold;
      font-size: 1.5rem !important;
      line-height: 2rem !important;
    `}
  span {
    font-size: ${({ isAiInterview }) =>
      isAiInterview ? '1.5rem !important' : '1.0625rem !important'};
    &.not-metric {
      font-size: ${({ isAiInterview }) =>
        isAiInterview ? '1.5rem !important' : '1.5rem !important'};
    }
    .metric {
      margin-left: 3px;
      font-size: ${({ isAiInterview }) =>
        isAiInterview ? '1.5rem !important' : '15px !important'};
      color: white;
    }
  }
  margin-right: 4px;
`;
const TimerWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: -2px;
  justify-content: center;
  color: white;
  padding: 1rem 0;
`;
const TimerIcon = styled.i`
  font-size: ${({ isAiInterview }) => isAiInterview && '30px !important'};
  margin-top: 2px;
  margin-right: 6px;
  display: inline;
`;
const TimerMetricWrapper = styled.span`
  &.timer-warning {
    color: red !important;
    .metric {
      color: red;
    }
  }
  .metric {
    margin-left: 3px;
    font-size: ${({ isAiInterview }) =>
      isAiInterview ? '1.5rem !important' : '15px !important'};
    color: white;
  }
  &.seconds {
    opacity: 0.7;
  }
`;

/**
 * Timer Component
 * Displays a timer that can either count up or down
 * @param {Object} props - Component props
 * @param {Function} props.CustomTimer - Optional custom timer component
 * @param {boolean} props.isIncrement - Whether the timer should increment or decrement
 * @returns {JSX.Element} The rendered timer
 */
const Timer = ({
  isAiInterview,
  CustomTimer,
  isIncrement = false,
  expiryTime,
  startTime,
  onExpiry,
}) => {
  const [isTimerStopped, setIsTimerStopped] = useState(false);
  const currentTime = getCurrentTime();

  const onTimeExpiry = useCallback(() => {
    setIsTimerStopped(true);
    onExpiry?.();
  }, [onExpiry]);

  const { days, hours, minutes, seconds, asMinutes } = useTimer(
    isIncrement ? startTime : expiryTime,
    onTimeExpiry,
    currentTime,
    isTimerStopped,
    setIsTimerStopped,
    isIncrement
  );

  if (CustomTimer) {
    const timerData = {
      days,
      hours,
      minutes,
      seconds,
      asMinutes,
    };

    return <CustomTimer timerData={timerData} />;
  }
  return (
    <TimerWrapper id="do-test-timer">
      <TimerIcon
        className={classNames('fa fa-clock-o', {
          'text-danger': !isIncrement && asMinutes() <= 5,
        })}
        isAiInterview={isAiInterview}
      />
      {days > 0 && (
        <TimerText isAiInterview={isAiInterview}>
          {days}
          <span className="metric">D</span>
        </TimerText>
      )}
      {(hours > 0 || days > 0) && (
        <TimerText isAiInterview={isAiInterview}>
          <span className={classNames({ 'not-metric': days <= 0 })}>
            {hours}
            <span className="metric">H</span>
          </span>
        </TimerText>
      )}
      <TimerText isAiInterview={isAiInterview}>
        <TimerMetricWrapper
          className={classNames({
            'timer-warning': !isIncrement && asMinutes() <= 5,
          })}
        >
          {minutes}
          <span className="metric">M</span>
        </TimerMetricWrapper>
      </TimerText>
      <TimerText isAiInterview={isAiInterview}>
        <TimerMetricWrapper
          className={classNames('seconds', {
            'timer-warning': !isIncrement && asMinutes() <= 5,
          })}
        >
          {seconds}
          <span className="metric">S</span>
        </TimerMetricWrapper>
      </TimerText>
    </TimerWrapper>
  );
};

export default Timer;
